import { Text } from '@linktr.ee/arbor/components/Text';
import { useFlags } from '@linktr.ee/feature-flags-mfe';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AuthDivider from '@shared/components/AuthDivider';
import AuthForm from '@shared/components/AuthForm';
import AuthLink from '@shared/components/AuthLink';
import Button from '@shared/components/Button';
import { CookiePrefs } from '@shared/components/CookiePrefs';
import Disclaimer from '@shared/components/Disclaimer';
import { InputText } from '@shared/components/InputText';
import Layout from '@shared/components/Layout';
import MarketingConsent from '@shared/components/MarketingConsent';
import SocialButton from '@shared/components/SocialButton';
import {
  getAuthIdentifierType,
  publishAuthIdentifierSubmittedEvent,
  publishSignupStartedEvent,
  publishSignupErrorEncounteredEvent,
} from '@shared/utils/analytics/events';
import {
  usePublishPageViewedEvent,
  usePublishScreenViewedEvent,
} from '@shared/utils/analytics/hooks';
import { validateEmail } from '@shared/utils/validation';
import { translateValidationError } from '@shared/utils/validationMessages';
import bannerSignupImage from '@universal-login/images/banner-signup-desktop.webp';
import { RegisterScreenProps } from '@universal-login/types/descope';

const RegisterScreen: React.FC<RegisterScreenProps> = ({
  onChange,
  onClickContinue,
  onClickGoogle,
  onClickApple,
  onClickLogin,
  errorText,
  context,
  resetError,
  prefilledUsername,
}) => {
  const { t } = useTranslation();
  usePublishScreenViewedEvent('Sign Up');
  usePublishPageViewedEvent();

  const initialEmail = context?.form?.email;
  const [email, setEmail] = useState(
    typeof initialEmail === 'string' ? initialEmail : ''
  );
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [showMarketingCheckbox, setShowMarketingCheckbox] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState<boolean | null>(null);
  const [emailRegexError, setEmailRegexError] = useState<string | undefined>();
  const { getFlag } = useFlags();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onChangeRef.current({ 'form.signupMarketingOptIn': 'null' });
    const showMarketingCheckbox = Boolean(
      getFlag('gdpr-marketing-consent-checkbox-signup-mfe')
    );
    setShowMarketingCheckbox(showMarketingCheckbox);

    if (showMarketingCheckbox) {
      setMarketingOptIn(false); // Sets the marketing checkbox to false/unchecked by default if the user is in a gdpr country
      onChangeRef.current({ 'form.signupMarketingOptIn': 'false' });
    }
  }, [getFlag]);

  const onMarketingOptInChange = (checked: boolean) => {
    setMarketingOptIn(checked);
    onChange({ 'form.signupMarketingOptIn': checked ? 'true' : 'false' });
  };

  const handleEmailChange = (value: string) => {
    // Clear errors when user starts typing
    resetError();
    setEmailRegexError(undefined);

    // Update email value
    setEmail(value);
    onChange({ email: value });
  };

  const handleEmailContinue = async (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();

    // Validate email before submitting
    if (email.trim()) {
      const validation = validateEmail(email.trim());
      if (!validation.isValid) {
        setEmailRegexError(validation.error);
        return;
      }
    }

    setIsLoading(true);
    try {
      publishAuthIdentifierSubmittedEvent(
        'signup',
        getAuthIdentifierType(email)
      );
      await onClickContinue(); // Continue button interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Signup failed';
      publishSignupErrorEncounteredEvent('Email', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClickLogin(); // Log in button interaction
  };

  const handleGoogleSignup = async () => {
    setGoogleLoading(true);
    try {
      publishSignupStartedEvent('Google');
      await onClickGoogle(); // Google signup interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Google signup failed';
      publishSignupErrorEncounteredEvent('Google', errorMessage);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAppleSignup = async () => {
    setAppleLoading(true);
    try {
      publishSignupStartedEvent('Apple');
      await onClickApple(); // Apple signup interaction
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Apple signup failed';
      publishSignupErrorEncounteredEvent('Apple', errorMessage);
    } finally {
      setAppleLoading(false);
    }
  };

  // Use proper email validation instead of simple check
  const isEmailValid = email.length > 0 && !emailRegexError;

  const heading = prefilledUsername
    ? t('Claim @{{username}} on Linktree today', {
        username: prefilledUsername,
      })
    : t('Join Linktree');

  return (
    <Layout
      heading={heading}
      subHeading={t('Sign up for free!')}
      variant="banner"
      bannerImageDesktop={bannerSignupImage}
    >
      <div className="space-y-6">
        <AuthForm>
          <InputText
            label={t('Email')}
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleEmailChange(e.target.value);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key === 'Enter' && isEmailValid && !isLoading) {
                e.preventDefault();
                const mockEvent = {
                  preventDefault: () => e.preventDefault(),
                } as React.MouseEvent<HTMLButtonElement>;
                handleEmailContinue(mockEvent);
              }
            }}
            errorMessage={
              translateValidationError(t, emailRegexError) || errorText
            }
            state={emailRegexError || errorText ? 'error' : 'default'}
          />
          <MarketingConsent
            show={showMarketingCheckbox}
            checked={marketingOptIn || false}
            onChange={onMarketingOptInChange}
          />
          <Button
            onClick={handleEmailContinue}
            variant="primary"
            disabled={!isEmailValid}
            label={t('Continue')}
            state={isLoading ? 'loading' : 'default'}
          />
        </AuthForm>

        <Disclaimer showMarketingCopy={!showMarketingCheckbox} center={true} />

        <AuthDivider />

        <div className="space-y-3">
          <SocialButton
            provider="google"
            onClick={handleGoogleSignup}
            loading={googleLoading}
          >
            {t('Continue with Google')}
          </SocialButton>
          <SocialButton
            provider="apple"
            onClick={handleAppleSignup}
            loading={appleLoading}
          >
            {t('Continue with Apple')}
          </SocialButton>
        </div>

        <div className="text-center mt-8">
          <Text as="p" variant="body-small-regular" className="text-gray-600">
            {t('Already have an account?')}{' '}
            <AuthLink>
              <button type="button" onClick={handleLogin}>
                {t('Log in')}
              </button>
            </AuthLink>
          </Text>
        </div>
      </div>
      <CookiePrefs />
    </Layout>
  );
};

export default RegisterScreen;
